// Journal/timeline shell for request-workspace audit-style entries. The
// shell renders a vertical timeline with a left rail + accent dot per
// entry; each entry slot accepts a `primary` first line and an optional
// `metadata` second line. Data sourcing, empty / loading / error wrappers,
// and entry copy stay feature-local. See
// `apps/web/docs/request-workspace-ui-contract.md`.

import type { ReactNode } from 'react';

// INVARIANT: every entry renders three children in this order — dot,
// primary line, metadata line. The request-workspace audit contract
// requires the metadata line (actor + role + timestamp), so `metadata`
// is required; future selectors may rely on `nth-child(3)` for the
// metadata row.
export interface JournalTimelineEntry {
  key: string;
  primary: ReactNode;
  metadata: ReactNode;
}

export interface JournalTimelineProps {
  entries: readonly JournalTimelineEntry[];
}

export function JournalTimeline({ entries }: JournalTimelineProps) {
  return (
    <div
      style={{
        borderLeft: '1px solid var(--border-1)',
        paddingLeft: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      {entries.map((e) => (
        <div key={e.key} style={{ position: 'relative' }}>
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: -22,
              top: 6,
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: 'var(--bg-1)',
              border: '2px solid var(--accent-1)',
            }}
          />
          <div style={{ fontSize: 13, color: 'var(--fg-1)' }}>{e.primary}</div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--fg-3)',
              marginTop: 2,
              display: 'flex',
              alignItems: 'baseline',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            {e.metadata}
          </div>
        </div>
      ))}
    </div>
  );
}
